import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, MapPin, Clock, Users, DollarSign, Grid3X3, 
  MessageCircle, User, ChevronRight, Ticket, PartyPopper,
  Briefcase, Heart, Dumbbell, Palette, UsersRound, Sparkles
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  pride: PartyPopper,
  party: Sparkles,
  meetup: UsersRound,
  networking: Briefcase,
  sports: Dumbbell,
  arts: Palette,
  community: Heart,
  dating: Heart
};

const categoryColors = {
  pride: 'bg-gradient-to-r from-red-500 via-yellow-500 to-green-500',
  party: 'bg-[#7000FF]',
  meetup: 'bg-[#08D9D6]',
  networking: 'bg-blue-500',
  sports: 'bg-green-500',
  arts: 'bg-pink-500',
  community: 'bg-[#FF2E63]',
  dating: 'bg-[#FF2E63]'
};

const Events = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [processingPayment, setProcessingPayment] = useState(false);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment verification timed out. Please check your registrations.');
      return;
    }

    setProcessingPayment(true);
    try {
      // Find the event from session - we need to get it from registrations
      const regResponse = await axios.get(`${API}/events/my/registrations`);
      const registration = regResponse.data.find(r => r.payment_session_id === sessionId);
      
      if (registration) {
        const response = await axios.get(`${API}/events/${registration.event_id}/payment-status?session_id=${sessionId}`);
        if (response.data.payment_status === 'paid') {
          toast.success('Payment successful! You\'re registered for the event.');
          setProcessingPayment(false);
          fetchData();
          // Clear URL params
          window.history.replaceState({}, '', '/events');
          return;
        }
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setProcessingPayment(false);
    }
  };

  const fetchData = async () => {
    try {
      const [eventsRes, registrationsRes] = await Promise.all([
        axios.get(`${API}/events${activeCategory !== 'all' ? `?category=${activeCategory}` : ''}`),
        axios.get(`${API}/events/my/registrations`)
      ]);
      setEvents(eventsRes.data);
      setMyRegistrations(registrationsRes.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeCategory]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const EventCard = ({ event }) => {
    const CategoryIcon = categoryIcons[event.category] || Calendar;
    const isRegistered = myRegistrations.some(r => r.event_id === event.id && ['confirmed', 'paid'].includes(r.status));
    
    return (
      <div 
        data-testid={`event-card-${event.id}`}
        className="bg-[#111] border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors cursor-pointer"
        onClick={() => navigate(`/events/${event.id}`)}
      >
        {/* Event Image or Category Banner */}
        <div className={`h-32 relative ${event.image_data ? '' : categoryColors[event.category] || 'bg-[#FF2E63]'}`}>
          {event.image_data ? (
            <img 
              src={event.image_data.startsWith('data:') ? event.image_data : `data:image/jpeg;base64,${event.image_data}`}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <CategoryIcon className="w-12 h-12 text-white/80" />
            </div>
          )}
          
          {/* Category badge */}
          <span className="absolute top-3 left-3 px-2 py-1 bg-black/50 backdrop-blur-sm rounded text-xs font-medium text-white capitalize">
            {event.category}
          </span>
          
          {/* Price badge */}
          <span className={`absolute top-3 right-3 px-2 py-1 rounded text-xs font-bold ${
            event.is_paid ? 'bg-[#FF2E63] text-white' : 'bg-[#22c55e] text-white'
          }`}>
            {event.is_paid ? `$${event.price}` : 'FREE'}
          </span>
          
          {/* Registered badge */}
          {isRegistered && (
            <div className="absolute bottom-3 right-3 px-2 py-1 bg-[#08D9D6] rounded text-xs font-bold text-black flex items-center gap-1">
              <Ticket className="w-3 h-3" />
              Registered
            </div>
          )}
        </div>
        
        {/* Event Info */}
        <div className="p-4">
          <h3 className="text-white font-semibold text-lg mb-2 line-clamp-1">{event.title}</h3>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <Calendar className="w-4 h-4 text-[#FF2E63]" />
              <span>{formatDate(event.event_date)}</span>
              <span className="text-[#52525B]">•</span>
              <Clock className="w-4 h-4 text-[#FF2E63]" />
              <span>{formatTime(event.start_time)}</span>
            </div>
            
            <div className="flex items-center gap-2 text-[#A1A1AA]">
              <MapPin className="w-4 h-4 text-[#08D9D6]" />
              <span className="truncate">{event.location_name}</span>
            </div>
            
            {event.capacity && (
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Users className="w-4 h-4 text-[#7000FF]" />
                <span>
                  {event.spots_left !== null ? (
                    event.spots_left > 0 ? `${event.spots_left} spots left` : 'Full'
                  ) : `${event.registered_count || 0} attending`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const categories = ['all', 'pride', 'party', 'meetup', 'networking', 'sports', 'arts', 'community'];

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold text-[#FF2E63]">Events</h1>
          <p className="text-sm text-[#A1A1AA]">Discover local LGBTQ+ events</p>
        </div>
        
        {/* Category Pills */}
        <div className="px-4 pb-3 overflow-x-auto">
          <div className="flex gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                data-testid={`category-${cat}`}
                onClick={() => { setActiveCategory(cat); setLoading(true); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat 
                    ? 'bg-[#FF2E63] text-white' 
                    : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                }`}
              >
                {cat === 'all' ? 'All Events' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Processing Payment Overlay */}
      {processingPayment && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="bg-[#111] p-6 rounded-xl text-center">
            <div className="w-12 h-12 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Verifying payment...</p>
            <p className="text-[#A1A1AA] text-sm">Please wait</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="p-4">
        <Tabs defaultValue="upcoming" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white/5 mb-4">
            <TabsTrigger value="upcoming" className="data-[state=active]:bg-[#FF2E63]">
              Upcoming
            </TabsTrigger>
            <TabsTrigger value="registered" className="data-[state=active]:bg-[#FF2E63]">
              My Events
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="upcoming">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="h-64 skeleton rounded-xl" />
                ))}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No events found</h3>
                <p className="text-[#A1A1AA]">Check back later for upcoming events</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="registered">
            {myRegistrations.length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">No registrations yet</h3>
                <p className="text-[#A1A1AA]">Browse events and register to see them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myRegistrations.map(reg => (
                  <div
                    key={reg.id}
                    className="bg-[#111] border border-white/10 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:border-white/20"
                    onClick={() => navigate(`/events/${reg.event_id}`)}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      reg.event?.category ? categoryColors[reg.event.category] : 'bg-[#FF2E63]'
                    }`}>
                      <Ticket className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{reg.event?.title || 'Event'}</h4>
                      <p className="text-sm text-[#A1A1AA]">
                        {reg.event?.event_date && formatDate(reg.event.event_date)}
                        {reg.status === 'waitlist' && ' • Waitlisted'}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      reg.status === 'paid' ? 'bg-[#22c55e]/20 text-[#22c55e]' :
                      reg.status === 'confirmed' ? 'bg-[#08D9D6]/20 text-[#08D9D6]' :
                      'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {reg.status}
                    </span>
                    <ChevronRight className="w-5 h-5 text-[#52525B]" />
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <BottomNav />
    </div>
  );
};

export default Events;
